import { GridColDef, GridRowOrderChangeParams } from '@mui/x-data-grid-premium';
import React, { useCallback, useMemo } from 'react';
import { FEATURE_CONSTANTS } from '../../../../constants/featureConstants';
import { useI18n } from '../../../../i18n/I18n';
import { Icons } from '../../../../icons/Icons';
import { H } from '../../../../Layout';
import DataGrid from '../../../../list/DataGrid';
import type { WorkEntryRow } from '../../../types';
import { reorderWorkEntryIds, workEntryFromGridParams } from '../../utils';
import WorkEntryItem from './WorkEntryItem';

interface WorkEntriesGridProps<TSource> {
    rows: ReadonlyArray<WorkEntryRow<TSource>>;
    orderedIds: ReadonlyArray<string>;
    enableReorder: boolean;
    enableSelection: boolean;
    showKeyIcon: boolean;
    keyEntryTooltip?: React.ReactNode;
    selectedIds: ReadonlySet<string>;
    onToggleEntry: (id: string) => void;
    onCommitOrder: (nextOrderedIds: string[]) => void;
    minCardColumnWidth: number;
    compactHorizontalPadding?: boolean;
    rowActionsEnabled?: boolean;
    onEdit?: (row: WorkEntryRow<TSource>) => void;
    onRequestDelete?: (row: WorkEntryRow<TSource>) => void;
}

export default function WorkEntriesGrid<TSource>({
    rows,
    orderedIds,
    enableReorder,
    enableSelection,
    showKeyIcon,
    keyEntryTooltip,
    selectedIds,
    onToggleEntry,
    onCommitOrder,
    minCardColumnWidth,
    compactHorizontalPadding = false,
    rowActionsEnabled = false,
    onEdit,
    onRequestDelete
}: WorkEntriesGridProps<TSource>) {
    const t = useI18n();

    const gridRows = useMemo(
        () =>
            rows.map((row) => ({
                id: row.id,
                __reorder__: row.primaryTitle
            })),
        [rows]
    );

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'entry',
                headerName: t('workEntries.column.entry'),
                flex: 1,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const entryId = String(params.id);
                    const row = workEntryFromGridParams(rows, params);
                    if (!row) return null;
                    const selected = enableSelection ? selectedIds.has(entryId) : !!row.isKey;
                    return (
                        <WorkEntryItem
                            row={row}
                            selected={selected}
                            showKeyIcon={showKeyIcon}
                            keyEntryTooltip={keyEntryTooltip}
                            toggleEntry={onToggleEntry}
                            showDragHandle={enableReorder}
                            showCheckbox={enableSelection}
                            marketplaceInnerItemPadding={compactHorizontalPadding}
                            rowActionsEnabled={rowActionsEnabled}
                            onEdit={onEdit}
                            onRequestDelete={onRequestDelete}
                        />
                    );
                }
            }
        ],
        [
            rows,
            enableSelection,
            showKeyIcon,
            keyEntryTooltip,
            enableReorder,
            selectedIds,
            onToggleEntry,
            compactHorizontalPadding,
            rowActionsEnabled,
            onEdit,
            onRequestDelete,
            t
        ]
    );

    const entriesGridSx = useMemo(
        () => ({
            border: 'none',
            px: compactHorizontalPadding ? 0 : 2,
            py: 1,
            '--DataGrid-rowBorderColor': 'transparent',
            height: 'auto !important',
            '& .MuiDataGrid-root': { height: 'auto !important' },
            '& .MuiDataGrid-columnHeaders': { display: 'none' },
            '& .MuiDataGrid-virtualScroller': { overflow: 'visible !important' },
            '& .MuiDataGrid-virtualScrollerContent': {
                height: 'auto !important',
                minHeight: '0 !important',
                flexBasis: 'auto !important'
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
                position: 'relative !important',
                top: '0 !important',
                transform: 'none !important',
                width: '100% !important',
                display: 'grid !important',
                gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minCardColumnWidth}px), 1fr))`,
                columnGap: 2,
                rowGap: 1
            },
            '& .MuiDataGrid-row': {
                position: 'relative !important',
                top: 'unset !important',
                left: 'unset !important',
                transform: 'none !important',
                width: 'auto !important',
                minWidth: '0 !important',
                maxWidth: 'none !important',
                alignItems: 'stretch',
                backgroundColor: 'transparent !important',
                border: 'none !important',
                borderBottom: 'none !important',
                overflow: 'visible'
            },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                px: '0 !important'
            },
            '& .MuiDataGrid-cell[data-field="entry"]': {
                width: '100% !important',
                minWidth: '0 !important',
                maxWidth: '100% !important',
                flexBasis: '100% !important',
                flexGrow: '1 !important',
                overflow: 'visible !important'
            },
            '& .MuiDataGrid-cell[data-field="__reorder__"]': {
                width: '0 !important',
                minWidth: '0 !important',
                maxWidth: '0 !important',
                p: '0 !important',
                m: 0,
                border: 'none',
                overflow: 'visible',
                pointerEvents: 'none',
                display: enableReorder ? undefined : 'none'
            },
            '& .MuiDataGrid-cell[data-field="__reorder__"] .MuiDataGrid-rowReorderCell': {
                position: 'absolute',
                left: 16,
                top: 24,
                width: 24,
                height: 24,
                p: 0,
                m: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
                pointerEvents: enableReorder ? 'auto' : 'none',
                cursor: 'grab'
            },
            '& .MuiDataGrid-cell[data-field="__reorder__"] .MuiDataGrid-rowReorderCellPlaceholder': {
                display: 'none'
            },
            '& .MuiDataGrid-rowReorderCell .MuiDataGrid-rowReorderIcon': {
                cursor: 'grab',
                opacity: 0
            },
            '& .MuiDataGrid-row, & .MuiDataGrid-cell': {
                outline: 'none !important'
            }
        }),
        [compactHorizontalPadding, enableReorder, minCardColumnWidth]
    );

    const handleRowOrderChange = useCallback(
        (params: GridRowOrderChangeParams) => {
            const visibleIds = rows.map((row) => row.id);
            const nextOrderedIds = reorderWorkEntryIds(orderedIds, visibleIds, params.oldIndex, params.targetIndex);
            if (!nextOrderedIds) return;
            onCommitOrder(nextOrderedIds);
        },
        [rows, orderedIds, onCommitOrder]
    );

    return (
        <H sx={{ width: '100%' }}>
            <DataGrid
                id="work-entries-list-grid"
                feature={FEATURE_CONSTANTS.peopleResources}
                rows={gridRows}
                rowCount={gridRows.length}
                columns={columns}
                asIsColumns={['entry']}
                includeActions={false}
                loading={false}
                serverPagination={false}
                disablePagination
                forceDefaultColumnVisibility
                defaultColumnVisibility={{ entry: true, __reorder__: true }}
                checkboxSelection={false}
                disableRowSelectionOnClick
                rowReordering={enableReorder}
                onRowOrderChange={handleRowOrderChange}
                hideFooter
                autoHeight
                disableVirtualization
                slots={enableReorder ? { rowReorderIcon: Icons.DragIndicator } : undefined}
                containerSx={entriesGridSx}
            />
        </H>
    );
}
